import { Link } from 'react-router-dom';
import { BuildingLibraryIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { teamApi } from '../../lib/api';
import { useTeamId } from '../../hooks/useTeamId';

/**
 * Omnipresent active-bank identity block (CLARID-IAF-001 — UX epic PR 11,
 * bank-as-primary-object IA).
 *
 * The data model is one-bank-per-team, but the active bank was near-invisible —
 * the only signal was a fragile team-name selector, which makes operating a
 * portfolio of banks (the Arcsalus model) low-confidence. This surfaces the
 * bank's legal name + LEI persistently in the shell so a consultant always
 * knows which bank they're configuring.
 *
 * Reads the canonical institution-setup record (PeerBenchmarkConfig-backed
 * identity) and shares the wizard's query key (`['institutionSetup', teamId]`)
 * so it updates the moment setup is saved, and evicts on team switch
 * (TeamSwitcher.removeQueries drops every key !== 'teams').
 */
export default function ActiveBankIndicator() {
  const { teamId } = useTeamId();
  const { data, isLoading } = useQuery({
    queryKey: ['institutionSetup', teamId],
    queryFn: () => teamApi.getInstitutionSetup(teamId!).then((r) => r.data),
    enabled: !!teamId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (!teamId) return null;

  const name = (data as { institutionName?: string | null } | undefined)?.institutionName;
  const lei = (data as { institutionLei?: string | null } | undefined)?.institutionLei;

  // Identity not configured yet → a quiet prompt routing to where it's set up.
  // This is also the on-ramp out of the "setup scattered across three screens"
  // problem the IA findings call out.
  if (!isLoading && !name && !lei) {
    return (
      <div className="px-3 pt-3 pb-1">
        <Link
          to="/settings"
          className="flex items-center gap-2.5 rounded-md border border-dashed border-rule px-3 py-2 text-xs text-ink-3 transition-colors hover:bg-panel-2 hover:text-ink"
        >
          <BuildingLibraryIcon className="h-4 w-4 shrink-0 text-ink-4" />
          <span className="truncate">Set up bank identity</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="px-3 pt-3 pb-1">
      <div className="flex items-center gap-2.5 rounded-md bg-panel-2 px-3 py-2">
        <BuildingLibraryIcon className="h-4 w-4 shrink-0 text-teal-600" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-ink" title={name ?? undefined}>
            {isLoading ? 'Loading…' : (name ?? 'Unnamed institution')}
          </div>
          {lei && (
            <div
              className="truncate font-mono text-[0.65rem] uppercase tracking-wide text-ink-4"
              title={`LEI ${lei}`}
            >
              LEI {lei}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
